import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { subscribeWithSelector } from 'zustand/middleware';

const useInstanceStore = create(
  subscribeWithSelector(
    persist(
      (set, get) => ({
        selectedInstanceId: null,
        instances: [],
        demoInstances: [],
        loading: false,
        error: null,
        previousInstanceId: null,

        setSelectedInstanceId: (instanceId) => {
          const currentId = get().selectedInstanceId;
          if (currentId === instanceId) return;
          
          console.log('🎯 Zustand: Setting selected instance ID:', instanceId);
          set({
            previousInstanceId: currentId,
            selectedInstanceId: instanceId,
            error: null
          });
        },

        setInstances: (instances) => {
          set({ instances });
          
          const current = get();
          if (!current.selectedInstanceId && instances.length > 0) {
            const firstReady = instances.find(i => i.status === 'ready') || instances[0];
            if (firstReady) {
              console.log('🎯 Zustand: Auto-selecting first instance:', firstReady.id);
              set({ selectedInstanceId: firstReady.id });
            }
          }
          
          if (current.selectedInstanceId && !instances.find(i => i.id === current.selectedInstanceId)) {
            console.warn('Selected instance no longer exists, clearing selection');
            set({ selectedInstanceId: null });
          }
        },

        setDemoInstances: (demoInstances) => {
          set({ demoInstances });
        },

        setLoading: (loading) => set({ loading }),

        setError: (error) => set({ error }),

        getSelectedInstance: () => {
          const { selectedInstanceId, instances, demoInstances } = get();
          // Check user instances first
          const userInstance = instances.find(instance => instance.id === selectedInstanceId);
          if (userInstance) return userInstance;
          // Then check demo instances
          return demoInstances.find(instance => instance.id === selectedInstanceId) || null;
        },

        getSelectedInstanceId: () => get().selectedInstanceId,

        clearSelection: () => {
          console.log('🎯 Zustand: Clearing instance selection');
          set({ 
            previousInstanceId: get().selectedInstanceId,
            selectedInstanceId: null 
          });
        },

        updateInstance: (instanceId, updates) => {
          set(state => ({
            instances: state.instances.map(instance =>
              instance.id === instanceId ? { ...instance, ...updates } : instance
            )
          }));
        },

        incrementPostCount: (instanceId) => {
          set(state => ({
            instances: state.instances.map(instance =>
              instance.id === instanceId 
                ? { ...instance, posts_count: (instance.posts_count || 0) + 1 }
                : instance
            )
          }));
        },

        updateProgressAndPosts: (instanceId, progress, postsGenerated) => {
          set(state => ({
            instances: state.instances.map(instance =>
              instance.id === instanceId ? { 
                ...instance, 
                progress,
                posts_count: postsGenerated || instance.posts_count || 0,
                status: progress >= 100 ? 'ready' : 'processing'
              } : instance
            )
          }));
        },

        removeInstance: (instanceId) => {
          set(state => {
            const newInstances = state.instances.filter(instance => instance.id !== instanceId);
            const newSelectedId = state.selectedInstanceId === instanceId ? null : state.selectedInstanceId;
            
            return {
              instances: newInstances,
              selectedInstanceId: newSelectedId,
              previousInstanceId: state.selectedInstanceId === instanceId ? state.previousInstanceId : state.previousInstanceId
            };
          });
        },

        syncWithSupabase: async () => {
          console.log('Supabase sync will be implemented in a future story');
        },

        validatePersistedInstance: async (instanceId) => {
          if (!instanceId) return false;
          
          const instances = get().instances;
          const exists = instances.some(i => i.id === instanceId);
          
          if (!exists && instances.length > 0) {
            console.warn(`Persisted instance ${instanceId} not found in available instances`);
            return false;
          }
          
          return true;
        },

        reset: () => {
          console.log('🔄 Resetting instance store');
          set({
            selectedInstanceId: null,
            instances: [],
            demoInstances: [],
            loading: false,
            error: null,
            previousInstanceId: null
          });

          // Clear localStorage
          localStorage.removeItem('scrolled-instance-store');
        }
      }),
      {
        name: 'scrolled-instance-store',
        storage: createJSONStorage(() => localStorage),
        partialize: (state) => ({
          selectedInstanceId: state.selectedInstanceId
        }),
        onRehydrateStorage: () => (state) => {
          if (state?.selectedInstanceId) {
            console.log('🎯 Zustand: Rehydrating with instance:', state.selectedInstanceId);
            state.validatePersistedInstance(state.selectedInstanceId).then(isValid => {
              if (!isValid && state.instances.length > 0) {
                console.warn('Persisted instance is invalid, clearing selection');
                state.clearSelection();
              }
            });
          }
        }
      }
    )
  )
);

// Development tools for debugging
if (process.env.NODE_ENV === 'development') {
  window.instanceStore = useInstanceStore;
  console.log('🔧 Instance Store mounted at window.instanceStore');
  console.log('Use window.instanceStore.getState() to inspect state');
  console.log('Use window.instanceStore.setState() to modify state');
}

export default useInstanceStore;